import * as React from 'react';

import classname from '@search/classname/src';
import { groupByCols } from '@search/helpers/src/groupByCols';
import Image from '@search/vtbeco-frontend-core/view/common/components/Image';
import { renderNewBuildingFlatStatus } from '@search/vtbeco-frontend-core/view/common/components/NewBuildingFlats';
import { NewBuildingClassBadge } from '@search/vtbeco-frontend-core/view/common/components/Badge/NewBuildingClassBadge';

import { OfferType } from '@search/vtbeco-frontend-core/domain/offer/search/page/OfferQuery';

import './styles.css';
import { NewBuildingFlat } from '@search/graphql-typings';
import { getNewBuildingLink } from '@search/vtbeco-frontend-core/view/selectors/get_new_building_link';
import { useClassifiedOfferDataLayerPush } from '@search/vtbeco-frontend-core/domain/offer/analytics/ClassifiedOfferGtmContext';

const cn = classname.bind(null, 'NewBuildingMiniCard');

type ArrayElement<A> = A extends readonly (infer T)[] ? T : never

const renderFlatsTable = (flats: Array<NewBuildingFlat>, index: number, flatsUrl: string) => {
    if (Array.isArray(flats)) {
        return (
            <div
                key={`flats-${flats.length}-${index}`}
                className={cn('flats')}
            >
                <table
                    className={cn('flats-table')}
                >
                    <tbody>
                        {
                            flats.map((flat, rowIndex) => {
                                return (
                                    <tr className={cn('flats-row')} key={rowIndex}>
                                        <td className={cn('flats-column')}><a href={flatsUrl}>{flat!.label}</a></td>
                                        <td
                                            className={cn('flats-column')}
                                        >
                                            <span color={flat?.priceMin ? '' : 'gray'}>
                                                {renderNewBuildingFlatStatus(flat)}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })
                        }
                    </tbody>
                </table>
            </div>
        );
    }

    return null;
};

const NewBuildingMiniCard: React.FunctionComponent<{
    newBuilding: OfferType['newBuildingCard'];
    skipFlats: boolean;
    gtmClick?: string;
}> = ({
    newBuilding,
    skipFlats = false,
    gtmClick
}) => {
    const dataLayerPush = useClassifiedOfferDataLayerPush();

    if (! newBuilding) {
        return null;
    }

    const url = getNewBuildingLink({
        ...newBuilding?.routeParams
    });

    let flats = null;
    let image = null;
    const flatsUrl = url + '#flats';

    if (newBuilding.flats?.length && ! skipFlats) {
        flats = (
            <div className={cn('flats-wrapper')}>
                {
                    groupByCols<ArrayElement<typeof newBuilding.flats>>(
                        newBuilding.flats
                            .filter(flat =>
                                flat &&
                                (flat?.isSoldOut || flat?.isSoon || flat?.isTemporaryNotOnSale || flat?.priceMin)
                            ),
                        2,
                        1
                    ).map((item, index) => renderFlatsTable(item, index, flatsUrl))
                }
            </div>
        );
    }

    if (newBuilding.gallery?.main?.large) {
        image = (
            <Image
                type='cover'
                width={64}
                height={64}
                className={cn('photo')}
                url={newBuilding.gallery?.main?.large} />
        );
    }

    return (
        <div className={cn()}>
            <a
                href={url}
                target='_blank'
                onClick={() => gtmClick && dataLayerPush({ event: gtmClick })}
                className={cn('link')}
                rel='noopener'
            />
            <div className={cn('section-info')}>
                <div className={cn('header')}>
                    {image}
                    <div className={cn('header-info')}>
                        <h4 className={cn('header-title')}>
                            <a href={url} target='_blank'>
                                {newBuilding.title.nominative}
                            </a>
                        </h4>
                        <div className={cn('status')}>
                            {newBuilding.buildingStatus?.reduce((
                                ret: Array<string | React.ReactNode>,
                                item,
                                idx
                            ) => {
                                if (idx % 2) {
                                    ret.push(
                                        <span key={idx} className={cn('separator')}>•</span>
                                    );
                                }

                                ret.push(item.title);

                                return ret;
                            }, [])}
                        </div>
                    </div>
                </div>
                {
                    newBuilding.buildingClass && (
                        <NewBuildingClassBadge
                            rightIndent={null}
                            newBuildingClass={newBuilding.buildingClass.value}>
                            {newBuilding.buildingClass.fullLabel}
                        </NewBuildingClassBadge>
                    )
                }
            </div>
            {flats}
        </div>
    );
};

export default NewBuildingMiniCard;
